import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  UserActivityEvent,
  UserActivityEventsGetParams,
} from '~/data/userActivity';
import { UserActivityApiClient } from '~/services/Http';
import {
  AddUserActivityEventsDto,
  GetUserActivityEventsParamsDto,
  IAddUserActivityEventsDto,
  IUserActivityEventListResultDto,
  UserActivityEventListResultDto,
} from '~/services/Http/client.iam.generated';

export type StoreState = {
  userActivity: UserActivityEvent[];
  isLoading: boolean;
  failedToLoad: boolean;
};

const initialState: StoreState = {
  userActivity: [],
  isLoading: false,
  failedToLoad: false,
};

export enum UserActivityActionKeyPrefixes {
  ADD = 'data/userActivity/ADD',
  GET = 'data/userActivity/GET',
}

type UserActivityPayload = Omit<IUserActivityEventListResultDto, 'data'> & {
  data: UserActivityEvent[];
};

export const fetchUserActivity = createAsyncThunk<
  UserActivityEventListResultDto,
  UserActivityEventsGetParams
>(
  UserActivityActionKeyPrefixes.GET,
  async (
    params: UserActivityEventsGetParams,
  ): Promise<UserActivityEventListResultDto> => {
    /**
     * NOTE: A bit experimental Swagger API implementation was done in BE,
     * codegeneration has a bug, and this is something of a workaround
     */
    return await UserActivityApiClient.list(
      JSON.stringify(new GetUserActivityEventsParamsDto(params).toJSON()),
    );
  },
);

export const addUserActivity = createAsyncThunk<
  void,
  IAddUserActivityEventsDto
>(
  UserActivityActionKeyPrefixes.ADD,
  async (userActivity: IAddUserActivityEventsDto): Promise<void> => {
    if (!userActivity) {
      return Promise.reject();
    }

    return UserActivityApiClient.addEvents(
      new AddUserActivityEventsDto(userActivity),
    );
  },
);

export const userActivitySlice = createSlice({
  name: '~/data/userActivity',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchUserActivity.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<UserActivityPayload>,
    ) => {
      state.failedToLoad = false;
      state.isLoading = false;
      state.userActivity = action.payload.data;
    },
    [fetchUserActivity.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [fetchUserActivity.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
    [addUserActivity.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<void>,
    ) => {
      // TODO: Should the API return something?
      state.failedToLoad = false;
      state.isLoading = false;
    },
    [addUserActivity.pending.type]: (state: StoreState) => {
      state.isLoading = true;
    },
    [addUserActivity.rejected.type]: (state: StoreState) => {
      state.failedToLoad = true;
    },
  },
});

export const userActivityReducer = userActivitySlice.reducer;
